import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Dropdown,
  Field,
  Input,
  Option,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';

import { MOCK_USER_NAMES } from '../../data';
import { TASK_TYPE_OPTIONS, TChangePlan, TTaskType } from '../../models';
import { workPackageForType } from '../../utils';
import { TAddTaskInput } from '../../state/ChangeV2Store';

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: '420px' },
  dateInput: {
    width: '100%',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    fontFamily: tokens.fontFamilyBase,
  },
});

type TProps = {
  plan: TChangePlan;
  open: boolean;
  onClose: () => void;
  onSubmit: (input: TAddTaskInput) => void;
};

export const AddTaskDialog = ({ plan, open, onClose, onSubmit }: TProps) => {
  const classes = useStyles();
  const [title, setTitle] = useState('');
  const [taskType, setTaskType] = useState<TTaskType>(TASK_TYPE_OPTIONS[0]);
  const [workPackageId, setWorkPackageId] = useState(plan.workPackages[0]?.id ?? '');
  const [owner, setOwner] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [required, setRequired] = useState(true);

  // Reset the form each time the dialog opens.
  useEffect(() => {
    if (open) {
      setTitle('');
      setTaskType(TASK_TYPE_OPTIONS[0]);
      setWorkPackageId(plan.workPackages[0]?.id ?? '');
      setOwner('');
      setDueDate('');
      setRequired(true);
    }
  }, [open, plan.workPackages]);

  const workPackageName = plan.workPackages.find((wp) => wp.id === workPackageId)?.name ?? '';

  const handleSubmit = () => {
    if (!title.trim()) return;
    onSubmit({
      title: title.trim(),
      taskType,
      workPackageId,
      owner: owner || undefined,
      dueDate: dueDate ? `${dueDate}T00:00:00.000Z` : undefined,
      required,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={onClose} />
              </DialogTrigger>
            }
          >
            Add task
          </DialogTitle>
          <DialogContent>
            <div className={classes.body}>
              <Field label="Title" required>
                <Input
                  value={title}
                  onChange={(_, data) => setTitle(data.value)}
                  placeholder="What needs to be done?"
                />
              </Field>
              <Field label="Task type">
                <Dropdown
                  selectedOptions={[taskType]}
                  value={taskType}
                  onOptionSelect={(_, data) => {
                    const next = data.optionValue as TTaskType;
                    setTaskType(next);
                    // Route the task into the work package that matches its type.
                    const suggested = plan.workPackages.find((wp) => wp.id === workPackageForType(next))?.id;
                    if (suggested) setWorkPackageId(suggested);
                  }}
                >
                  {TASK_TYPE_OPTIONS.map((type) => (
                    <Option key={type} value={type} text={type}>
                      {type}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              <Field label="Work package">
                <Dropdown
                  selectedOptions={[workPackageId]}
                  value={workPackageName}
                  onOptionSelect={(_, data) => setWorkPackageId(data.optionValue ?? workPackageId)}
                >
                  {plan.workPackages.map((wp) => (
                    <Option key={wp.id} value={wp.id} text={wp.name}>
                      {wp.name}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              <Field label="Owner">
                <Dropdown
                  selectedOptions={owner ? [owner] : ['']}
                  value={owner || 'Unassigned'}
                  onOptionSelect={(_, data) => setOwner(data.optionValue || '')}
                >
                  <Option value="" text="Unassigned">
                    Unassigned
                  </Option>
                  {MOCK_USER_NAMES.map((name) => (
                    <Option key={name} value={name} text={name}>
                      {name}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              <Field label="Due date">
                <input
                  type="date"
                  className={classes.dateInput}
                  value={dueDate}
                  onChange={(event) => setDueDate(event.target.value)}
                />
              </Field>
              <Checkbox
                checked={required}
                label="Required for the plan to be ready"
                onChange={(_, data) => setRequired(Boolean(data.checked))}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={!title.trim()} onClick={handleSubmit}>
              Add task
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
